import { CSSProperties, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isMobileBrowser } from 'utils/browserDetect';
import { debounce } from 'utils/debounce';
import { isDefined } from 'utils/ramda';
import { createReactiveVar } from 'utils/reactivity';
import { Maybe } from 'utils/types';

import { getBlockAnchorId, getPageAnchorId } from '../../utils/itineraries/functions/utils';
import { ILandingV2Context, TSetActivePageIdArgs, useLandingV2Context } from './context';
import { EPageLayout } from './Page/types';
import {
  ELandingDeviceType,
  ELandingHeaderLayout,
  EMapType,
  ILandingConfig,
  ILandingHeaderConfig,
  ILandingV2Props,
  IMapConfig,
  INavBarConfig,
  TLandingPagesConfig,
} from './types';

export function useLandingV2ContextValue(props: ILandingV2Props): ILandingV2Context {
  const {
    rightActions,
    leftActions,
    config,
    data,
    activePageId: externalActivePageId = data.landing.pages[0].id,
    defaultAnchor,
    onSetActivePageId,
    readonly = true,
    deviceType: externalDeviceType,
    pageHandlersProvider = ProxyComponent,
    blockHandlersProvider = ProxyComponent,
    isBuilder,
    isPrivate,
  } = props;

  // need to prevent recreating reactive var
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reactActivePageId = useMemo(() => createReactiveVar(externalActivePageId), []);
  const isManualScrollRef = useRef(false);
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const landingRef = useRef(null);
  const isFirstRender = useRef(true);
  const landingConfig = useMemo(() => config || getDefaultLandingV2Config(), [config]);
  const deviceType = useDeviceType(landingRef, externalDeviceType);
  const setActivePageId = useCallback(
    ({ pageId, notifyExternalEnv, withScroll }: TSetActivePageIdArgs) => {
      if (onSetActivePageId && notifyExternalEnv) onSetActivePageId(pageId);
      else reactActivePageId(pageId);

      if (withScroll) scrollToPage(contentRef.current, getPageAnchorId(pageId));
    },
    [onSetActivePageId, reactActivePageId],
  );

  useEffect(() => {
    if (!externalActivePageId || (isFirstRender.current && externalActivePageId === data.landing.pages[0].id)) return;

    isFirstRender.current = false;

    setActivePageId({ pageId: externalActivePageId, withScroll: !isManualScrollRef.current });
  }, [externalActivePageId, reactActivePageId, setActivePageId, data.landing.pages[0].id]);

  // scroll only once to anchor page id if it's defined
  useEffect(() => {
    if (!defaultAnchor?.pageId) return;

    setTimeout(() => {
      scrollToPage(contentRef.current, getPageAnchorId(defaultAnchor.pageId!));
    }, 0);
    // needs only after first rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // scroll only once to anchor block id if it's defined
  useEffect(() => {
    if (!defaultAnchor?.blockId) return;

    setTimeout(() => {
      scrollToBlock(contentRef.current, headerRef.current, getBlockAnchorId(defaultAnchor.blockId!), landingConfig);
    }, 1000);
    // needs only after first rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => ({
      data,
      readonly,
      config: landingConfig,
      deviceType,
      isManualScrollRef,
      rightActions,
      leftActions,
      contentRef,
      headerRef,
      landingRef,
      pageHandlersProvider,
      blockHandlersProvider,
      reactActivePageId,
      setActivePageId,
      isBuilder,
      isPrivate,
    }),
    [
      data,
      readonly,
      landingConfig,
      deviceType,
      isManualScrollRef,
      leftActions,
      rightActions,
      contentRef,
      headerRef,
      landingRef,
      pageHandlersProvider,
      blockHandlersProvider,
      reactActivePageId,
      setActivePageId,
      isBuilder,
      isPrivate,
    ],
  );
}

export function getDefaultLandingV2Config(): ILandingConfig {
  return {
    header: getDefaultHeaderConfig(),
    pages: getDefaultPagesConfig(),
    guestFormDisabled: false,
    css: '',
    map: getDefaultMapConfig(),
  };
}

export function getLandingV2Config(config: Partial<ILandingConfig>): ILandingConfig {
  return {
    ...getDefaultLandingV2Config(),
    ...config,
  };
}

function scrollToPage(container: Maybe<HTMLDivElement>, anchorId: string) {
  if (container === null) return;

  scrollToAnchor(container, anchorId, container.getBoundingClientRect().top);
}

function scrollToBlock(
  container: Maybe<HTMLDivElement>,
  header: Maybe<HTMLDivElement>,
  anchorId: string,
  config: ILandingConfig,
) {
  if (container === null) return;

  let headerHeight = 0;
  if (header && config.header && config.header.layout === ELandingHeaderLayout.HalfWidth) {
    headerHeight = header.getBoundingClientRect().height;
  }

  const PAGE_HEADER_TITLE_HEIGHT = 80;
  // it refers to padding css property of .blocksContent className of RightSide component
  const PAGE_TOP_PADDING = 36;
  const top = container.getBoundingClientRect().top + PAGE_HEADER_TITLE_HEIGHT + PAGE_TOP_PADDING + headerHeight;

  scrollToAnchor(container, anchorId, top);
}

function scrollToAnchor(container: HTMLDivElement, anchorId: string, top: number = 0) {
  const link = document.getElementById(anchorId);

  if (!link) return;

  setTimeout(() => {
    container.scrollTo({
      top: container.scrollTop - top + (link.getBoundingClientRect().top || 0),
      behavior: 'smooth',
    });
  }, 0);
}

export function useManualScrollWatcher() {
  const { isManualScrollRef, contentRef } = useLandingV2Context();
  const isMobilePlatform = isMobileBrowser();

  useEffect(() => {
    if (contentRef.current === null) return;
    const contentElem = contentRef.current;

    /*
      handlePointerDown, handlePointerMove, handlePointerUp are used for mobile devices mostly
     */
    const handlePointerDown = () => {
      // console.log('pointer event down'); // leave the console.log for fast debugging in case of bugs
      contentElem.addEventListener('pointermove', handlePointerMove);
      contentElem.addEventListener('pointerup', handlePointerUp);
    };
    const handlePointerMove = () => {
      // console.log('pointer event move');
      if (!isManualScrollRef.current) {
        // console.log('==========> manual scroll start when pointer move');
        isManualScrollRef.current = true;

        contentElem.addEventListener('scroll', stopManualScrolling);
      }

      if (isManualScrollRef.current) {
        stopManualScrolling();
      }
    };
    const handlePointerUp = () => {
      // console.log('pointer up');
      contentElem.removeEventListener('pointermove', handlePointerMove);
    };

    const stopManualScrolling = debounce(() => {
      if (isManualScrollRef.current) {
        // console.log('==================> manual scroll stop');
        isManualScrollRef.current = false;

        contentElem.removeEventListener('scroll', stopManualScrolling);
      }
    }, 600);

    /*
      handleMouseWheel used for listening mouse wheel event
     */
    const handleMouseWheel = () => {
      // console.log('wheeel');
      if (!isManualScrollRef.current) {
        // console.log('====================> manual scroll start');
        isManualScrollRef.current = true;

        contentElem.addEventListener('scroll', stopManualScrolling);
      }

      // try to stop manual scrolling when wheel event fires, bc scroll event is not fired always (e.g. if you try
      // to scroll up in a top, or scroll down at the bottom of the page)
      if (isManualScrollRef.current) {
        stopManualScrolling();
      }
    };

    contentElem.addEventListener('pointerdown', handlePointerDown);
    contentElem.addEventListener('wheel', handleMouseWheel);

    if (isMobilePlatform) {
      contentElem.addEventListener('scroll', handlePointerMove);
    }

    return () => {
      contentElem.removeEventListener('pointerdown', handlePointerDown);
      contentElem.removeEventListener('wheel', handleMouseWheel);

      if (isMobilePlatform) {
        contentElem.removeEventListener('scroll', handlePointerMove);
      }
    };
  }, [isManualScrollRef, contentRef, isMobilePlatform]);
}

const ProxyComponent = ({ children }: { children: Function }) => children({});

export function useContentProps() {
  const { contentRef, config } = useLandingV2Context();
  const [height, setHeight] = useState(0);

  const setHeightCb = useCallback(() => {
    if (!contentRef.current) return;

    setHeight(contentRef.current?.getBoundingClientRect().height);
  }, [contentRef]);

  useEffect(() => {
    setHeightCb();
    window.addEventListener('resize', setHeightCb);

    return () => {
      window.removeEventListener('resize', setHeightCb);
    };
  }, [setHeightCb]);

  // this useEffect is used for changing header layout config in custom css
  useEffect(() => {
    setHeightCb();
  }, [config.header, setHeightCb]);

  return {
    style: { '--contentHeight': height + 'px' } as CSSProperties,
  };
}

export function useDeviceType(
  landingRef: MutableRefObject<HTMLDivElement | null>,
  forcedDeviceType?: ELandingDeviceType,
) {
  const [deviceType, setDeviceType] = useState<ELandingDeviceType>(forcedDeviceType || ELandingDeviceType.Desktop);
  const handleSetDeviceType = useCallback(() => {
    if (landingRef.current === null) return;

    const rect = landingRef.current.getBoundingClientRect();

    if (rect.width > 768 && deviceType !== ELandingDeviceType.Desktop) setDeviceType(ELandingDeviceType.Desktop);
    if (rect.width <= 768 && deviceType !== ELandingDeviceType.Mobile) setDeviceType(ELandingDeviceType.Mobile);
  }, [landingRef, deviceType]);

  const listener = useCallback(() => {
    if (isDefined(forcedDeviceType)) return;

    handleSetDeviceType();
  }, [handleSetDeviceType, forcedDeviceType]);

  useEffect(() => {
    if (isDefined(forcedDeviceType)) return;

    handleSetDeviceType();
  }, [handleSetDeviceType]);

  useEffect(() => {
    window.addEventListener('resize', listener);

    return () => window.removeEventListener('resize', listener);
  }, [listener]);

  return deviceType;
}

export function getDefaultTopBarConfig() {
  return { leftActions: true };
}

export function getDefaultNavBarConfig(): INavBarConfig {
  return { mobile: true, desktop: false };
}

export function getDefaultHeaderConfig(): ILandingHeaderConfig {
  return {
    layout: ELandingHeaderLayout.HalfWidth,
    navBar: getDefaultNavBarConfig(),
    topBar: getDefaultTopBarConfig(),
  };
}

export function getHeaderConfig(
  partial: Partial<ILandingHeaderConfig> = {},
): ReturnType<typeof getDefaultHeaderConfig> {
  return {
    ...getDefaultHeaderConfig(),
    ...partial,
  };
}

function getDefaultPagesConfig(): TLandingPagesConfig {
  return [{ layout: EPageLayout.HalfWidth }];
}

function getDefaultMapConfig(): IMapConfig {
  return {
    type: EMapType.Roadmap,
  };
}
